import { useState, useMemo } from 'react';

import type { EmpresaPlataforma } from '@/mocks/backoffice/empresas';
import { campanhasBackoffice } from '@/mocks/backoffice/campanhasBackoffice';
import { configuracoesCampanhas, valoresPadraoPlataforma } from '@/mocks/backoffice/configuracoesCampanhas';
import { participanteCampanhaConfigs } from '@/mocks/backoffice/participanteCampanhaConfig';

type CategoriaConfig = 'taxas' | 'limites' | 'regras' | 'geral';

const categorias: { id: CategoriaConfig; label: string; icon: string; descricao: string }[] = [
  { id: 'taxas', label: 'Taxas', icon: 'ri-percent-line', descricao: 'Taxas de processamento, compra e transferência' },
  { id: 'limites', label: 'Limites', icon: 'ri-ruler-line', descricao: 'Limites de resgate, transferência e pontos' },
  { id: 'regras', label: 'Regras', icon: 'ri-file-list-3-line', descricao: 'Pontuação, bonificação, prazos e verificações' },
  { id: 'geral', label: 'Geral', icon: 'ri-settings-3-line', descricao: 'Email, moeda e moderação' },
];

interface EmpresaPlataformaConfigProps {
  empresa: EmpresaPlataforma;
  configs: Record<string, { valor: string; descricao: string; tipo: string; categoria: string }>;
  onUpdate: (chave: string, valor: string) => void;
}

function getValorDisplay(valor: string, tipo: string) {
  if (tipo === 'boolean') {
    return valor === 'true' ? (
      <span className="inline-flex items-center space-x-1 text-emerald-600">
        <div className="w-4 h-4 flex items-center justify-center"><i className="ri-check-line text-sm"></i></div>
        <span className="text-xs font-bold">Ativo</span>
      </span>
    ) : (
      <span className="inline-flex items-center space-x-1 text-gray-400">
        <div className="w-4 h-4 flex items-center justify-center"><i className="ri-close-line text-sm"></i></div>
        <span className="text-xs font-bold">Inativo</span>
      </span>
    );
  }
  if (tipo === 'porcentagem') return <span className="text-sm font-bold text-gray-800">{valor}%</span>;
  if (tipo === 'numero') return <span className="text-sm font-bold text-gray-800">{Number(valor).toLocaleString('pt-BR')}</span>;
  return <span className="text-sm text-gray-800">{valor}</span>;
}

function getTipoInput(tipo: string) {
  switch (tipo) {
    case 'boolean': return 'checkbox';
    case 'numero':
    case 'porcentagem': return 'number';
    default: return 'text';
  }
}

export default function EmpresaPlataformaConfig({ empresa, configs, onUpdate }: EmpresaPlataformaConfigProps) {
  const [activeCategoria, setActiveCategoria] = useState<CategoriaConfig>('taxas');
  const [editando, setEditando] = useState<string | null>(null);
  const [editValor, setEditValor] = useState('');

  const configsFiltradas = Object.entries(configs).filter(([, c]) => c.categoria === activeCategoria);

  // Calcula uso de cada config (campanhas + participantes)
  const usoPorConfig = useMemo(() => {
    const uso: Record<string, { campanhas: number; participantes: number }> = {};

    Object.keys(configs).forEach((chave) => {
      // Campanhas desta empresa que sobrescrevem esta config
      const campSobre = campanhasBackoffice.filter((c) => {
        if (c.empresaId !== empresa.id) return false;
        const cfg = configuracoesCampanhas.find((cf) => cf.campanhaId === c.id);
        if (!cfg || cfg.herdaPlataforma) return false;
        // Verifica se esta campanha sobrescreve esta chave específica
        if (chave === 'taxa_processamento_pix' && cfg.taxas.taxa_processamento_pix !== valoresPadraoPlataforma.taxas.taxa_processamento_pix) return true;
        if (chave === 'limite_resgate_diario' && cfg.limites.limite_resgate_diario !== valoresPadraoPlataforma.limites.limite_resgate_diario) return true;
        if (chave === 'limite_resgate_mensal' && cfg.limites.limite_resgate_mensal !== valoresPadraoPlataforma.limites.limite_resgate_mensal) return true;
        if (chave === 'prazo_aprovacao_saque' && cfg.regras.prazo_aprovacao_saque !== valoresPadraoPlataforma.regras.prazo_aprovacao_saque) return true;
        return false;
      }).length;

      // Participantes desta empresa com config personalizada nesta chave
      let partSobre = 0;
      const configsEmpresa = participanteCampanhaConfigs.filter((c) => c.empresaId === empresa.id && c.ativo);
      configsEmpresa.forEach((pc) => {
        if (chave === 'limiteResgateDiario' && pc.limiteResgateDiarioPersonalizado !== undefined) partSobre++;
        else if (chave === 'limiteResgateMensal' && pc.limiteResgateMensalPersonalizado !== undefined) partSobre++;
        else if (chave === 'taxaProcessamentoPix' && pc.taxaProcessamentoPixPersonalizada !== undefined) partSobre++;
        else if (chave === 'prazoAprovacaoSaque' && pc.prazoAprovacaoSaquePersonalizado !== undefined) partSobre++;
      });

      uso[chave] = { campanhas: campSobre, participantes: partSobre };
    });

    return uso;
  }, [empresa.id, configs]);

  const handleEdit = (chave: string, valor: string) => {
    setEditando(chave);
    setEditValor(valor);
  };

  const handleSave = (chave: string) => {
    onUpdate(chave, editValor);
    setEditando(null);
  };

  const handleCancel = () => {
    setEditando(null);
    setEditValor('');
  };

  const categoriaAtual = categorias.find((c) => c.id === activeCategoria);

  const statsResumo = useMemo(() => {
    const todasConfigs = Object.entries(configs);
    const comCampanhasSobre = todasConfigs.filter(([chave]) => usoPorConfig[chave]?.campanhas > 0).length;
    const comParticipantesSobre = todasConfigs.filter(([chave]) => usoPorConfig[chave]?.participantes > 0).length;
    return {
      totalConfigs: todasConfigs.length,
      configsAtivas: comCampanhasSobre,
      configsPersonalizadas: comParticipantesSobre,
      campanhasDaEmpresa: campanhasBackoffice.filter((c) => c.empresaId === empresa.id).length,
      participantesDaEmpresa: participanteCampanhaConfigs.filter((c) => c.empresaId === empresa.id && c.ativo).length,
    };
  }, [configs, usoPorConfig, empresa.id]);

  return (
    <div className="space-y-5">
      {/* Header da empresa */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-building-line text-emerald-600 text-lg"></i>
            </div>
          </div>
          <div>
            <p className="text-sm font-bold text-gray-800">{empresa.nome_fantasia}</p>
            <p className="text-xs text-gray-500">
              Configurações base que as campanhas desta empresa herdam por padrão
            </p>
          </div>
        </div>
      </div>

      {/* Painel de Resumo de Uso */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center space-x-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-stack-line text-blue-600 text-sm"></i></div>
            </div>
            <p className="text-[10px] font-bold text-gray-500 uppercase">Total de Configs</p>
          </div>
          <p className="text-2xl font-bold text-gray-900">{statsResumo.totalConfigs}</p>
          <p className="text-[10px] text-gray-400 mt-1">configurações base da empresa</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center space-x-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-briefcase-line text-amber-600 text-sm"></i></div>
            </div>
            <p className="text-[10px] font-bold text-gray-500 uppercase">Campanhas</p>
          </div>
          <p className="text-2xl font-bold text-gray-900">{statsResumo.campanhasDaEmpresa}</p>
          <p className="text-[10px] text-gray-400 mt-1">
            <span className="text-amber-600 font-bold">{statsResumo.configsAtivas}</span> configs sobrescritas
          </p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center space-x-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-user-star-line text-emerald-600 text-sm"></i></div>
            </div>
            <p className="text-[10px] font-bold text-gray-500 uppercase">Participantes</p>
          </div>
          <p className="text-2xl font-bold text-gray-900">{statsResumo.participantesDaEmpresa}</p>
          <p className="text-[10px] text-gray-400 mt-1">
            <span className="text-emerald-600 font-bold">{statsResumo.configsPersonalizadas}</span> configs personalizadas
          </p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center space-x-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center">
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-shield-check-line text-purple-600 text-sm"></i></div>
            </div>
            <p className="text-[10px] font-bold text-gray-500 uppercase">Taxa de Uso</p>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {statsResumo.totalConfigs > 0
              ? Math.round(((statsResumo.configsAtivas + statsResumo.configsPersonalizadas) / (statsResumo.totalConfigs * 2)) * 100)
              : 0}%
          </p>
          <p className="text-[10px] text-gray-400 mt-1">configs com sobrescrita</p>
        </div>
      </div>

      {/* Categorias */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {categorias.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setActiveCategoria(cat.id)}
            className={`flex items-center space-x-3 px-4 py-3 rounded-xl border text-left transition-all duration-200 cursor-pointer ${
              activeCategoria === cat.id ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-gray-100 hover:border-gray-200'
            }`}
          >
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${activeCategoria === cat.id ? 'bg-emerald-100' : 'bg-gray-50'}`}>
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${cat.icon} ${activeCategoria === cat.id ? 'text-emerald-600' : 'text-gray-400'} text-lg`}></i>
              </div>
            </div>
            <div>
              <p className={`text-sm font-bold ${activeCategoria === cat.id ? 'text-emerald-700' : 'text-gray-700'}`}>{cat.label}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">
                {Object.values(configs).filter((c) => c.categoria === cat.id).length} configurações
              </p>
            </div>
          </button>
        ))}
      </div>

      {/* Lista */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <div className="flex items-center space-x-3">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className={`${categoriaAtual?.icon} text-emerald-600 text-sm`}></i>
            </div>
            <div>
              <h2 className="text-sm font-bold text-gray-800">{categoriaAtual?.label}</h2>
              <p className="text-xs text-gray-400">{categoriaAtual?.descricao}</p>
            </div>
          </div>
        </div>
        <div className="divide-y divide-gray-50">
          {configsFiltradas.map(([chave, config]) => {
            const uso = usoPorConfig[chave] || { campanhas: 0, participantes: 0 };
            return (
              <div key={chave} className="px-5 py-4 flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <p className="text-sm font-bold text-gray-800">{config.descricao}</p>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 font-bold uppercase">{config.tipo}</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1 font-mono">{chave}</p>
                  {/* Painel de uso inline */}
                  <div className="flex items-center gap-3 mt-2">
                    {uso.campanhas > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 font-bold">
                        <i className="ri-briefcase-line text-[10px]"></i>
                        {uso.campanhas} campanha{uso.campanhas !== 1 ? 's' : ''} sobrescrevem
                      </span>
                    )}
                    {uso.participantes > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 font-bold">
                        <i className="ri-user-star-line text-[10px]"></i>
                        {uso.participantes} participante{uso.participantes !== 1 ? 's' : ''} personalizou
                      </span>
                    )}
                    {uso.campanhas === 0 && uso.participantes === 0 && (
                      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-gray-50 text-gray-400 font-bold">
                        <i className="ri-check-line text-[10px]"></i>
                        Padrão — nenhuma sobrescrita
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-3 flex-shrink-0">
                  {editando === chave ? (
                    <div className="flex items-center space-x-2">
                      {config.tipo === 'boolean' ? (
                        <input
                          type="checkbox"
                          checked={editValor === 'true'}
                          onChange={(e) => setEditValor(e.target.checked ? 'true' : 'false')}
                          className="w-5 h-5 rounded border-gray-300 text-emerald-600"
                        />
                      ) : (
                        <input
                          type={getTipoInput(config.tipo)}
                          value={editValor}
                          onChange={(e) => setEditValor(e.target.value)}
                          className="w-32 px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                          step={config.tipo === 'porcentagem' || config.tipo === 'numero' ? '0.01' : undefined}
                        />
                      )}
                      <button onClick={() => handleSave(chave)} className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-600 cursor-pointer">
                        <div className="w-4 h-4 flex items-center justify-center"><i className="ri-check-line text-sm"></i></div>
                      </button>
                      <button onClick={handleCancel} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 cursor-pointer">
                        <div className="w-4 h-4 flex items-center justify-center"><i className="ri-close-line text-sm"></i></div>
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-3">
                      <div className="text-right">{getValorDisplay(config.valor, config.tipo)}</div>
                      <button onClick={() => handleEdit(chave, config.valor)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 cursor-pointer">
                        <div className="w-4 h-4 flex items-center justify-center"><i className="ri-pencil-line text-sm"></i></div>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {configsFiltradas.length === 0 && (
            <div className="px-5 py-12 text-center text-gray-400 text-sm">Nenhuma configuração nesta categoria.</div>
          )}
        </div>
      </div>
    </div>
  );
}